import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { RootState } from "store";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import { Prescription } from "common/models/types";
import { Flex, InfoIconButton, Spinner } from "components/atoms";
import { MIN_APPROVAL_INFO_COUNT } from "common/constants";
import { canShowDrugLabel } from "components/molecules/DrugLabelInfo/drugLabelButton";
import { openDrugLabelSlide } from "components/molecules/DrugLabelInfo/openDrugLabelSlide";
import { DrugNameLink } from "components/molecules/DrugLabelInfo/DrugNameLink";

interface OTCCardProps {
  prescription: Prescription;
  referenceChecker?: {
    [id: string]: {
      count: number;
      totalLength?: number;
      totalBytes?: number;
    };
  };
  onEdit?: (prescriptionId: number) => void;
  onDelete?: (prescriptionId: number) => void;
}

const Card = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  padding: 12px 16px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: background 0.2s;

  &:hover {
    background: ${COLOR_SYSTEM.get("Gray")?.[10]};
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const OTCCard: React.FC<OTCCardProps> = ({ prescription, referenceChecker, onEdit, onDelete }) => {
  // 이번 세션에 허가사항을 열어본(캐시된) 약은 약품명 회색 처리 (visited link 패턴)
  const visited = useSelector(
    (s: RootState) => !!prescription.itemCode && s.data.drugLabels[prescription.itemCode] !== undefined
  );
  const showLabel = canShowDrugLabel(prescription.itemCode, referenceChecker);
  return (
    <Card>
      <CardContent>
        <Flex align="center" gap="8px">
          <DrugNameLink
            $clickable={showLabel}
            $visited={visited}
            onClick={
              showLabel
                ? () =>
                    openDrugLabelSlide({
                      itemCode: prescription.itemCode!,
                      productName: prescription.medicationName,
                    })
                : undefined
            }
          >
            <Text textType="Body1" style={{ fontWeight: 500 }}>
              {prescription.medicationName || "N/A"}
            </Text>
          </DrugNameLink>
          {referenceChecker && !prescription.itemCode && (
            <InfoIconButton
              iconName="Information Circle"
              iconColor={COLOR_SYSTEM.get("Red")[400]}
              tooltip="의약품이 정확히 입력되지 않았습니다. 수정 버튼을 눌러 의약품명을 검색하고 목록에서 선택해야 합니다."
            />
          )}
          {referenceChecker &&
            prescription.itemCode &&
            referenceChecker?.[prescription.itemCode]?.count === -1 && <Spinner size="Small" />}
          {referenceChecker &&
            prescription.itemCode &&
            referenceChecker?.[prescription.itemCode]?.count >= 0 &&
            referenceChecker?.[prescription.itemCode]?.count < MIN_APPROVAL_INFO_COUNT && (
              <InfoIconButton
                iconName="Information Circle"
                iconColor={COLOR_SYSTEM.get("Red")[400]}
                tooltip="허가사항 정보가 부족합니다. 클릭하여 식약처 사이트에서 취소/취하 여부를 확인하세요."
                onClick={() => {
                  window.open(
                    `https://nedrug.mfds.go.kr/pbp/CCBBB01/getItemDetail?itemSeq=${prescription.itemCode}`,
                    "_blank"
                  );
                }}
              />
            )}
        </Flex>
        {prescription.notes && (
          <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")?.[500]}>
            {prescription.notes}
          </Text>
        )}
      </CardContent>
      {(onEdit || onDelete) && (
        <ActionButtons>
          {onEdit && prescription.id && (
            <Button
              size="Small"
              type="Icon"
              variant="Outlined"
              color="Secondary"
              icon="Edit 01"
              onClick={() => onEdit(prescription.id!)}
            />
          )}
          {onDelete && prescription.id && (
            <Button
              size="Small"
              type="Icon"
              variant="Outlined"
              color="Negative"
              icon="Trash"
              onClick={() => onDelete(prescription.id!)}
            />
          )}
        </ActionButtons>
      )}
    </Card>
  );
};

export default OTCCard;
